import { DefaultValues } from 'react-hook-form';
import makeForm from '../../../../components/Form';
import { Atividade, ItemSaldo, Talhao, TalhaoRow } from '../../../../@types';
import { AtividadesService } from '../../../../services';
import maximumDecimalDigits from '../../../../utils/maximum-decimal-digits';
import Yup from '../../../../utils/yup';
import { getAreaDisponivel } from './utils';

export const TALHAO_VALIDATION_SCHEMA = Yup.object<TalhaoRow>().shape({
  talhao: Yup.object().required(),
  itemSaldo: Yup.object()
    .required()
    .when(
      ['talhao', '$talhoes', '$editingTalhaoIdentification'],
      (params, schema) => {
        const [talhao, talhoes, editingTalhaoIdentification] = params as [
          Talhao,
          TalhaoRow[],
          string,
        ];

        if (!talhao?.id || !talhoes) {
          return schema;
        }

        return schema.test(
          'unique',
          'Cultivar já inserida para este talhão',
          (value) => {
            const identification = `${talhao?.id}-${(value as ItemSaldo)?.id}`;

            if (identification === editingTalhaoIdentification) return true;

            const insumoJaAdicionadoAoTalhao = talhoes.find(
              (talhaorow) =>
                talhaorow.itemSaldo?.id === (value as ItemSaldo)?.id &&
                talhaorow.talhao?.id === talhao?.id,
            );

            return !insumoJaAdicionadoAoTalhao;
          },
        );
      },
    ),
  unidade: Yup.object()
    .nullable()
    .when('$sementeCobertura', {
      is: false,
      then: (schema) => schema.required(),
    }),
  area: Yup.number()
    .positive()
    .required()
    .when(
      ['talhao', '$talhoes', '$editingTalhaoIdentification'],
      (params, schema) => {
        const [talhao, talhoes, editingTalhaoIdentification] = params as [
          Talhao,
          TalhaoRow[],
          string,
        ];

        if (!talhao || !talhoes) return schema;

        const areaDisponivel = getAreaDisponivel(
          talhoes.filter(
            ({ identification }) =>
              identification !== editingTalhaoIdentification,
          ),
          talhao,
        );

        return schema.max(maximumDecimalDigits(areaDisponivel, 2));
      },
    ),
  densidade: Yup.number()
    .nullable()
    .when('$sementeCobertura', {
      is: false,
      then: (schema) => schema.positive().required(),
    }),
  pms: Yup.number().when(
    ['$atividade', '$sementeCobertura'],
    (params, schema) => {
      const [atividade, sementeCobertura] = params as [
        Atividade | undefined,
        boolean,
      ];

      if (AtividadesService.isMilho(atividade?.descricao) || sementeCobertura) {
        return schema.nullable().optional();
      }

      return schema.positive().required();
    },
  ),
  pg: Yup.number()
    .nullable()
    .when('$sementeCobertura', {
      is: false,
      then: (schema) => schema.positive().max(100).required(),
    }),
  ciclo: Yup.number().positive().required(),
  dataInicio: Yup.date().required(),
  dataTermino: Yup.date()
    .required()
    .min(Yup.ref('dataInicio'), 'Não pode ser menor que a data inicial'),
  dose: Yup.number()
    .nullable()
    .when('$sementeCobertura', {
      is: true,
      then: (schema) => schema.required(),
    }),
});

export const DEFAULT_VALUES: DefaultValues<TalhaoRow> = {
  talhao: null,
  itemSaldo: null,
  unidade: null,
  area: null,
  densidade: null,
  pms: null,
  pg: null,
  ciclo: null,
  dataInicio: null,
  dataTermino: null,
  identification: null,
  dose: null,
  dataTerminoMinima: null,
};

export const FormTalhao = makeForm<TalhaoRow>();
